import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { Settings, Globe, User, CreditCard, Key } from "lucide-react";
import { cn } from "@/lib/utils";
import GeneralSettings from "./components/GeneralSettings";
import EnvVariablesSettings from "./components/EnvVariablesSettings";
import DomainsSettings from "./components/DomainsSettings";
import SettingsSection from "./components/SettingsSection";
import AccountSettings from "@/screens/app/user-settings/components/AccountSettings";
import PlansAndBillingSettings from "@/screens/app/user-settings/components/PlansAndBillingSettings";
import { CommonService } from "@/services";
import "./ProjectSettingsDialog.scss";

interface ProjectSettingsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    initialCategory?: string;
}

interface SettingsCategory {
    id: string;
    title: string;
    icon: React.ReactNode;
    description: string;
}

interface SettingsSection {
    id: string;
    title: string;
    categories: SettingsCategory[];
}

type SettingsCategoryId = 'general' | 'env-vars' | 'domains' | 'user-account' | 'plans-billing';

// Constants
const DEFAULT_CATEGORY: SettingsCategoryId = 'general';

const settingsSections: SettingsSection[] = [
    {
        id: 'project-section',
        title: 'Project',
        categories: [
            {
                id: 'general',
                title: 'Project Settings',
                icon: <Settings className="w-5 h-5" />,
                description: 'Basic project settings and information'
            },
            {
                id: 'domains',
                title: 'Domains',
                icon: <Globe className="w-5 h-5" />,
                description: 'Configure custom domains and DNS settings'
            },
            {
                id: 'env-vars',
                title: 'Environment Variables',
                icon: <Key className="w-5 h-5" />,
                description: 'Manage API keys and environment variables'
            }
        ]
    },
    {
        id: 'user-section',
        title: 'User',
        categories: [
            {
                id: 'user-account',
                title: 'Account',
                icon: <User className="w-5 h-5" />,
                description: 'Manage your account settings and preferences'
            },
            {
                id: 'plans-billing',
                title: 'Plans & Billing',
                icon: <CreditCard className="w-5 h-5" />,
                description: 'Manage your subscription and billing information'
            }
        ]
    }
];

const ProjectSettingsDialog = ({ isOpen, onClose, initialCategory }: ProjectSettingsDialogProps) => {
    const isMobile = useIsMobile();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeCategory, setActiveCategory] = useState<SettingsCategoryId>(initialCategory as SettingsCategoryId || DEFAULT_CATEGORY);

    // Callbacks
    const handleCategoryChange = useCallback((categoryId: SettingsCategoryId) => {
        // Track settings category change
        CommonService.handleMixpanelTrack('settings_category_changed', {
            category_id: categoryId,
            previous_category: activeCategory,
            timestamp: new Date().toISOString()
        });

        setActiveCategory(categoryId);
        if (isOpen) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('settings', categoryId);
            setSearchParams(newParams);
        }
    }, [isOpen, setSearchParams, activeCategory, searchParams]);

    // URL parameter 'settings' will persist even after closing the dialog
    const handleClose = useCallback(() => {
        // Track settings dialog close
        CommonService.handleMixpanelTrack('settings_dialog_closed', {
            active_category: activeCategory,
            timestamp: new Date().toISOString()
        });

        // Remove settings parameter from URL when dialog closes using functional update
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('settings');
            return newParams;
        });
        onClose();
    }, [onClose, activeCategory, setSearchParams]);

    // Handle initial state and URL sync when the dialog opens or params change
    useEffect(() => {
        if (!isOpen) return;

        const categoryFromUrl = searchParams.get('settings') as SettingsCategoryId;
        const allCategories = settingsSections.flatMap(section => section.categories);
        const hasValidUrlCategory = categoryFromUrl && allCategories.find(cat => cat.id === categoryFromUrl);


        console.log("hasValidUrlCategory", hasValidUrlCategory);
        if (hasValidUrlCategory) {
            // If URL has a valid category, ensure state matches URL
            if (activeCategory !== categoryFromUrl) {
                setActiveCategory(categoryFromUrl);
            }
        } else if (hasValidUrlCategory === false) {
            // If URL doesn't have a valid category, prioritize initialCategory prop or default
            const targetCategory = (initialCategory as SettingsCategoryId) || DEFAULT_CATEGORY;
            setActiveCategory(targetCategory);

            // Sync URL with the target category
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set('settings', targetCategory);
                return newParams;
            }, { replace: true });
        }
    }, [isOpen, searchParams, setSearchParams, initialCategory]);

    // Track event when dialog opens
    useEffect(() => {
        if (isOpen) {
            const categoryFromUrl = searchParams.get('settings');
            CommonService.handleMixpanelTrack('settings_dialog_opened', {
                initial_category: categoryFromUrl || initialCategory || activeCategory,
                timestamp: new Date().toISOString()
            });
        }
    }, [isOpen]);

    const renderSettingsContent = () => {
        switch (activeCategory) {
            case 'general':
                return <GeneralSettings />;
            case 'domains':
                return <DomainsSettings />;
            case 'env-vars':
                return <EnvVariablesSettings />;
            case 'user-account':
                return <AccountSettings />;
            case 'plans-billing':
                return <PlansAndBillingSettings />;
            default:
                return (
                    <div className="settings-content">
                        <h3>Settings</h3>
                        <p className="text-[var(--muted-foreground)]">Select a category to configure settings</p>
                    </div>
                );
        }
    };

    if (isMobile) {
        return (
            <Drawer open={isOpen} onOpenChange={handleClose}>
                <DrawerContent className="h-[90vh] p-0">
                    <div className="flex flex-col h-full">
                        {/* Mobile Category Selector */}
                        <div className="mobile-category-selector px-6 py-4 border-b space-y-2">
                            <Label className="text-sm font-medium text-muted-foreground ml-1">
                                Settings Category
                            </Label>
                            <Select value={activeCategory} onValueChange={(value) => {
                                // Track mobile category selector change
                                CommonService.handleMixpanelTrack('settings_mobile_category_changed', {
                                    category_id: value,
                                    previous_category: activeCategory,
                                    timestamp: new Date().toISOString()
                                });
                                handleCategoryChange(value as SettingsCategoryId);
                            }}>
                                <SelectTrigger className="w-full h-11">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {settingsSections.map((section) => (
                                        <div key={section.id}>
                                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                {section.title}
                                            </div>
                                            {section.categories.map((category) => (
                                                <SelectItem key={category.id} value={category.id}>
                                                    <div className="flex items-center gap-2">
                                                        {category.title}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </div>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground ml-1">
                                {settingsSections.flatMap(s => s.categories).find(c => c.id === activeCategory)?.description}
                            </p>
                        </div>

                        {/* Mobile Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {renderSettingsContent()}
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="project-settings-dialog">
                <div className="settings-container">
                    <div className="settings-sidebar">
                        <nav className="space-y-4">
                            {settingsSections.map((section) => (
                                <div key={section.id} className="space-y-1">
                                    <div className="text-xs font-semibold text-[var(--muted-foreground)] px-3 py-1">
                                        {section.title}
                                    </div>
                                    {section.categories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => {
                                                // Track desktop sidebar category click
                                                CommonService.handleMixpanelTrack('settings_sidebar_category_clicked', {
                                                    category_id: category.id,
                                                    category_title: category.title,
                                                    section_title: section.title,
                                                    previous_category: activeCategory,
                                                    timestamp: new Date().toISOString()
                                                });
                                                handleCategoryChange(category.id as SettingsCategoryId);
                                            }}
                                            className={cn(
                                                "w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors text-sm font-normal",
                                                activeCategory === category.id
                                                    ? "bg-[var(--button-background-highlight)] text-white"
                                                    : "text-[var(--foreground)] hover:bg-[var(--button-background-highlight)] hover:text-white"
                                            )}
                                        >
                                            {category.icon}
                                            <div>{category.title}</div>
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </nav>
                    </div>

                    <div className="settings-main">
                        {renderSettingsContent()}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectSettingsDialog;
